import React, { useState, useEffect, useRef } from "react";
import { fmtTime, fmtEventName, colorForCode } from "../utils/analyticsUtils";

export default function LiveFeedTab({ data, live, pollInterval = 5000, onFetchLatest }) {
  const [events, setEvents] = useState(() => data?.events?.items ?? []);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (data?.events?.items) setEvents(data.events.items);
  }, [data]);

  useEffect(() => {
    if (!live || !onFetchLatest) return;
    intervalRef.current = setInterval(async () => {
      try {
        const fresh = await onFetchLatest();
        if (fresh?.events?.items?.length) {
          setEvents(prev => {
            const existing = new Set(prev.map(e => e._id));
            const newOnes  = fresh.events.items.filter(e => !existing.has(e._id));
            return newOnes.length ? [...newOnes, ...prev].slice(0, 50) : prev;
          });
        }
      } catch (_) {}
    }, pollInterval);
    return () => clearInterval(intervalRef.current);
  }, [live, onFetchLatest, pollInterval]);

  const srcColors = { user:"#3B82F6", device:"#10B981", system:"#6366F1" };
  const srcBg     = { user:"#EFF6FF",  device:"#ECFDF5",  system:"#EEF2FF" };

  return (
    <div className="ua-card p">
      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:18, paddingBottom:14, borderBottom:"1px solid rgba(99,102,241,.08)" }}>
        <div>
          <span className="ua-card-title">Live event feed</span>
          <span className="ua-card-sub">{events.length} events loaded</span>
        </div>
        {live ? (
          <div className="ua-live-ind">
            <div className="ua-live-ind-dot" />
            Live · polling every {pollInterval/1000}s
          </div>
        ) : (
          <span style={{ fontSize:12, color:"#9B99C4", fontFamily:"'DM Sans',sans-serif" }}>Paused</span>
        )}
      </div>

      {/* Table head */}
      <div style={{
        display:"grid", gridTemplateColumns:"180px 1fr 120px 80px 70px 60px",
        gap:12, padding:"8px 14px",
        background:"#F8F7FF", borderRadius:10, marginBottom:6,
        fontSize:10, fontWeight:800, color:"#9B99C4",
        letterSpacing:".08em", textTransform:"uppercase",
      }}>
        <span>Event</span><span>Screen</span>
        <span>Time (IST)</span><span>Source</span>
        <span>Platform</span><span>Code</span>
      </div>

      {/* Rows */}
      {events.length === 0
        ? <p style={{ color:"#9B99C4", fontSize:13, padding:"16px 14px" }}>No events yet.</p>
        : events.map((ev, i) => {
            const color = colorForCode(ev.event_code);
            const src   = ev.source ?? "system";
            return (
              <div key={ev._id} style={{
                display:"grid", gridTemplateColumns:"180px 1fr 120px 80px 70px 60px",
                alignItems:"center", gap:12,
                padding:"10px 14px", borderRadius:10,
                transition:"background .15s", cursor:"default",
                animation: i===0 ? "ua-drop .3s ease" : undefined,
              }}
              onMouseEnter={e => e.currentTarget.style.background="#F8F7FF"}
              onMouseLeave={e => e.currentTarget.style.background="transparent"}
              >
                <span style={{ fontSize:11, fontWeight:700, padding:"3px 10px", borderRadius:999, background:color+"18", color, border:`1px solid ${color}33`, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                  {fmtEventName(ev.event_name)}
                </span>
                <span style={{ color:"#5B5A8A", fontSize:12, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{ev.screen ?? "—"}</span>
                <span style={{ color:"#9B99C4", fontFamily:"monospace", fontSize:11 }}>{fmtTime(ev.timestamp)}</span>
                <span style={{ fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:999, background:srcBg[src]??"#F1F5F9", color:srcColors[src]??"#475569", textAlign:"center" }}>{src}</span>
                <span style={{ color:"#9B99C4", fontSize:12, textTransform:"capitalize" }}>{ev.device_info?.platform ?? "—"}</span>
                <span style={{ fontSize:10, fontWeight:600, padding:"2px 6px", borderRadius:4, background:"#EEF2FF", color:"#4F46E5", border:"1px solid rgba(99,102,241,.15)", fontFamily:"monospace", textAlign:"center" }}>{ev.event_code}</span>
              </div>
            );
          })
      }
    </div>
  );
}